import { useList, useCustomMutation } from '@refinedev/core';
import { useState, useMemo } from 'react';
import { notify } from '@/lib/notify';
import {
  AlertTriangle,
  Clock,
  Eye,
  Filter,
  User,
  VolumeX,
  Ban,
  UserX,
  Trash2,
  CheckCircle,
  XCircle,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import {
  type ViolationWithDetails,
  type ViolationType,
  VIOLATION_TYPE_LABELS,
  VIOLATION_TYPE_COLORS,
  VIOLATION_CONTEXT_LABELS,
} from './types';

/**
 * Форматирование даты
 */
function formatDate(dateString: string | null) {
  if (!dateString) {return '—';}
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Получить имя пользователя
 */
function getUserName(user: { firstName: string | null; lastName: string | null; username: string | null; id: number }): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  if (name) {return name;}
  if (user.username) {return `@${user.username}`;}
  return `Пользователь #${user.id}`;
}

/**
 * Иконка типа нарушения
 */
function getViolationIcon(type: ViolationType) {
  switch (type) {
    case 'WARNING':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'MUTE':
      return <VolumeX className="h-4 w-4 text-orange-500" />;
    case 'KICK':
      return <UserX className="h-4 w-4 text-red-500" />;
    case 'BAN':
      return <Ban className="h-4 w-4 text-destructive" />;
    case 'MESSAGE_DELETED':
      return <Trash2 className="h-4 w-4 text-muted-foreground" />;
    default:
      return <AlertTriangle className="h-4 w-4" />;
  }
}

/**
 * Форматирование длительности
 */
function formatDuration(minutes: number | null): string {
  if (!minutes) {return 'Бессрочно';}
  if (minutes < 60) {return `${minutes} мин.`;}
  if (minutes < 1440) {return `${Math.round(minutes / 60)} ч.`;}
  return `${Math.round(minutes / 1440)} дн.`;
}

/**
 * Панель деталей нарушения
 */
function ViolationDetailsSheet({
  violation,
  open,
  onClose,
  onRevoke,
}: {
  violation: ViolationWithDetails | null;
  open: boolean;
  onClose: () => void;
  onRevoke: () => void;
}) {
  if (!violation) {return null;}

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[600px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {getViolationIcon(violation.violationType)}
            Нарушение #{violation.id}
          </SheetTitle>
          <SheetDescription>
            <div className="flex items-center gap-2">
              <Badge variant={VIOLATION_TYPE_COLORS[violation.violationType]}>
                {VIOLATION_TYPE_LABELS[violation.violationType]}
              </Badge>
              <Badge variant="outline">
                {VIOLATION_CONTEXT_LABELS[violation.context]}
              </Badge>
              <Badge variant={violation.isActive ? 'default' : 'secondary'}>
                {violation.isActive ? 'Активно' : 'Отменено'}
              </Badge>
            </div>
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="space-y-4 pr-4">
            {/* Пользователь */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Нарушитель
                </CardTitle>
              </CardHeader>
              <CardContent>
                {violation.user ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">{getUserName(violation.user)}</div>
                      {violation.user.username && (
                        <div className="text-sm text-muted-foreground">@{violation.user.username}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="text-muted-foreground">Пользователь #{violation.userId}</span>
                )}
              </CardContent>
            </Card>

            {/* Причина */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Причина</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{violation.reason}</p>
              </CardContent>
            </Card>

            {/* Детали */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Детали</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Контекст:</span>
                  <span>{VIOLATION_CONTEXT_LABELS[violation.context]}</span>
                </div>
                {violation.contextEntityId && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ID сущности:</span>
                    <span>#{violation.contextEntityId}</span>
                  </div>
                )}
                {violation.relatedMessageId && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ID сообщения:</span>
                    <span>#{violation.relatedMessageId}</span>
                  </div>
                )}
                {violation.duration && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Длительность:</span>
                    <span>{formatDuration(violation.duration)}</span>
                  </div>
                )}
                {violation.expiresAt && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Истекает:</span>
                    <span>{formatDate(violation.expiresAt)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Администратор */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Выдал нарушение</CardTitle>
              </CardHeader>
              <CardContent>
                {violation.admin ? (
                  <div className="text-sm">
                    <span className="font-medium">{violation.admin.username}</span>
                    <span className="text-muted-foreground ml-2">({violation.admin.email})</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">Админ #{violation.adminId}</span>
                )}
                <div className="text-xs text-muted-foreground mt-1">
                  {formatDate(violation.createdAt)}
                </div>
              </CardContent>
            </Card>

            {/* Отмена */}
            {violation.revokedAt && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Undo2 className="h-4 w-4" />
                    Отменено
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {violation.revokedByAdmin && (
                    <div className="text-sm">
                      <span className="font-medium">{violation.revokedByAdmin.username}</span>
                      <span className="text-muted-foreground ml-2">({violation.revokedByAdmin.email})</span>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {formatDate(violation.revokedAt)}
                  </div>
                  {violation.revokeReason && (
                    <div className="text-sm mt-2">
                      <span className="text-muted-foreground">Причина: </span>
                      {violation.revokeReason}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Кнопка отмены */}
            {violation.isActive && (
              <Button
                variant="outline"
                className="w-full"
                onClick={onRevoke}
              >
                <Undo2 className="h-4 w-4 mr-2" />
                Отменить нарушение
              </Button>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Диалог отмены нарушения
 */
function RevokeDialog({
  open,
  onClose,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) {return;}
    onConfirm(reason);
    setReason('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Отменить нарушение</DialogTitle>
          <DialogDescription>
            Нарушение будет деактивировано. Укажите причину отмены.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="revoke-reason">Причина *</Label>
          <Textarea
            id="revoke-reason"
            placeholder="Например: Ошибочно выдано..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-2"
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={isLoading || !reason.trim()}
          >
            {isLoading ? 'Отмена...' : 'Подтвердить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Подсчёт активных фильтров
 */
function getActiveFiltersCount(typeFilter: string, contextFilter: string, isActiveFilter: string): number {
  let count = 0;
  if (typeFilter !== 'all') {count++;}
  if (contextFilter !== 'all') {count++;}
  if (isActiveFilter !== 'all') {count++;}
  return count;
}

/**
 * Страница списка нарушений
 */
export function ViolationsList() {
  useScrollRestoration();
  const [page, setPage] = usePersistedState('violations:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('violations:sort', {
    field: 'createdAt',
    order: 'desc',
  });
  const [search, setSearch] = usePersistedState('violations:search', '');
  const [typeFilter, setTypeFilter] = usePersistedState<string>('violations:typeFilter', 'all');
  const [contextFilter, setContextFilter] = usePersistedState<string>('violations:contextFilter', 'all');
  const [isActiveFilter, setIsActiveFilter] = usePersistedState<string>('violations:isActiveFilter', 'all');
  const [selectedViolation, setSelectedViolation] = useState<ViolationWithDetails | null>(null);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const pageSize = 20;

  const activeFiltersCount = getActiveFiltersCount(typeFilter, contextFilter, isActiveFilter);

  // Подготовка фильтров
  const queryFilters = useMemo(() => {
    const result: Array<{ field: string; operator: 'eq'; value: string }> = [];
    if (typeFilter !== 'all') {
      result.push({ field: 'violationType', operator: 'eq', value: typeFilter });
    }
    if (contextFilter !== 'all') {
      result.push({ field: 'context', operator: 'eq', value: contextFilter });
    }
    if (isActiveFilter !== 'all') {
      result.push({ field: 'isActive', operator: 'eq', value: isActiveFilter });
    }
    // Поиск по userId (числовое поле, бэкенд поддерживает фильтр userId)
    if (search.trim() && /^\d+$/.test(search.trim())) {
      result.push({ field: 'userId', operator: 'eq', value: search.trim() });
    }
    return result;
  }, [typeFilter, contextFilter, isActiveFilter, search]);

  // Загрузка нарушений
  const listResult = useList<ViolationWithDetails>({
    resource: 'violations',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;
  const refetch = listResult.query.refetch;

  // Мутация для отмены
  const revokeMutation = useCustomMutation();

  const handleRevoke = (reason: string) => {
    if (!selectedViolation) {return;}

    revokeMutation.mutate({
      url: `/admin/violations/${selectedViolation.id}/revoke`,
      method: 'post',
      values: { reason },
    }, {
      onSuccess: () => {
        setRevokeDialogOpen(false);
        setSelectedViolation(null);
        refetch();
      },
    });
  };

  const handleResetFilters = () => {
    setTypeFilter('all');
    setContextFilter('all');
    setIsActiveFilter('all');
    setSearch('');
    setPage(1);
  };

  // Bulk-действия
  const bulkActions: BulkAction<ViolationWithDetails>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          setSelectedViolation(items[0]);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Отменить нарушение',
      icon: <Undo2 className="h-4 w-4" />,
      onClick: (items) => {
        const active = items.filter((v) => v.isActive);
        if (active.length === 0) {
          notify.info('Нет активных нарушений среди выбранных');
          return;
        }
        if (items.length === 1 && items[0].isActive) {
          setSelectedViolation(items[0]);
          setRevokeDialogOpen(true);
        } else {
          notify.info('Для отмены нескольких нарушений откройте каждое по отдельности');
        }
      },
      showOnlyWhenSelected: true,
    },
  ];

  // Колонки таблицы
  const columns: Column<ViolationWithDetails>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '70px',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-xs text-muted-foreground">#{String(value)}</span>
      ),
    },
    {
      key: 'violationType',
      header: 'Тип',
      width: '180px',
      sortable: true,
      render: (_, item) => (
        <div className="flex items-center gap-2">
          {getViolationIcon(item.violationType)}
          <Badge variant={VIOLATION_TYPE_COLORS[item.violationType]}>
            {VIOLATION_TYPE_LABELS[item.violationType]}
          </Badge>
        </div>
      ),
    },
    {
      key: 'user',
      header: 'Пользователь',
      width: '200px',
      render: (_, item) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-3.5 w-3.5 text-primary" />
          </div>
          <span className="text-sm truncate">
            {item.user ? getUserName(item.user) : `#${item.userId}`}
          </span>
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Причина',
      render: (value) => (
        <span className="text-sm line-clamp-2">{String(value)}</span>
      ),
    },
    {
      key: 'context',
      header: 'Контекст',
      width: '120px',
      render: (_, item) => (
        <Badge variant="outline">
          {VIOLATION_CONTEXT_LABELS[item.context]}
        </Badge>
      ),
    },
    {
      key: 'isActive',
      header: 'Статус',
      width: '120px',
      render: (value) => value ? (
        <Badge variant="default" className="bg-green-600">
          <CheckCircle className="h-3 w-3 mr-1" />
          Активно
        </Badge>
      ) : (
        <Badge variant="secondary">
          <XCircle className="h-3 w-3 mr-1" />
          Отменено
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Дата',
      width: '160px',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(value as string)}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (_, item) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setSelectedViolation(item)}>
            <Eye className="h-4 w-4" />
          </Button>
          {item.isActive && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedViolation(item);
                setRevokeDialogOpen(true);
              }}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Нарушения"
        description="История всех нарушений пользователей"
        sticky
        icon={<AlertTriangle className="h-5 w-5" />}
      />

      {/* Поиск и фильтры */}
      <div className="flex items-center gap-2 flex-wrap">
        <TableToolbar
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Поиск по ID пользователя..."
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Фильтры
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Тип нарушения</Label>
                <Select
                  value={typeFilter}
                  onValueChange={(v) => {
                    setTypeFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все типы</SelectItem>
                    <SelectItem value="WARNING">Предупреждение</SelectItem>
                    <SelectItem value="MUTE">Мут</SelectItem>
                    <SelectItem value="KICK">Исключение</SelectItem>
                    <SelectItem value="BAN">Бан</SelectItem>
                    <SelectItem value="MESSAGE_DELETED">Удаление сообщения</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Контекст</Label>
                <Select
                  value={contextFilter}
                  onValueChange={(v) => {
                    setContextFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="CHAT">Чат</SelectItem>
                    <SelectItem value="GAME">Игра</SelectItem>
                    <SelectItem value="GLOBAL">Глобально</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Статус</Label>
                <Select
                  value={isActiveFilter}
                  onValueChange={(v) => {
                    setIsActiveFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="true">Активные</SelectItem>
                    <SelectItem value="false">Отменённые</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={handleResetFilters}
                >
                  Сбросить фильтры
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Таблица */}
      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(violation) => setSelectedViolation(violation)}
        emptyMessage="Нет нарушений"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      {/* Панель деталей */}
      <ViolationDetailsSheet
        violation={selectedViolation}
        open={!!selectedViolation && !revokeDialogOpen}
        onClose={() => setSelectedViolation(null)}
        onRevoke={() => setRevokeDialogOpen(true)}
      />

      {/* Диалог отмены */}
      <RevokeDialog
        open={revokeDialogOpen}
        onClose={() => setRevokeDialogOpen(false)}
        onConfirm={handleRevoke}
        isLoading={revokeMutation.mutation.isPending}
      />
    </div>
  );
}
